// src/pages/HomePage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FileBadge,
  Scale,
  NotebookPen,
  ScanSearch,
  Briefcase,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Menu,
  X,
  Zap,
  Lock,
  Phone,
  MapPin,
  Clock,
  Building2,
  Users,
  UserCheck,
  Rocket,
  ShieldCheck,
  BrainCircuit,
  Server,
  Plug,
  UploadCloud,
  Sparkles,
  FileOutput,
  Plus,
  Minus,
  Trophy,
} from 'lucide-react';
import styles from './HomePage.module.css';
import logo from '../assets/images/zone_icon.png';
import HeroDemo from '../components/home/HeroDemo';
import ProductVideo from '../components/home/ProductVideo';
import {
  useAnimationGate,
  useSceneLoop,
  rankRows,
  scoreTone,
} from '../components/home/useDemoAnimation';

/* ═══════════════════════════════════════════════════════════
   DONNÉES
   ═══════════════════════════════════════════════════════════ */

const NAV_LINKS = [
  { href: '#video',    label: 'Vidéo' },
  { href: '#how',      label: 'Comment ça marche' },
  { href: '#modules',  label: 'Modules' },
  { href: '#audience', label: 'Pour qui' },
  { href: '#security', label: 'Sécurité' },
  { href: '#faq',      label: 'FAQ' },
];

// Chiffres vérifiables, tirés du produit réel — pas de promesse marketing creuse.
const PROOF_STATS = [
  { value: '5',  label: 'modules',    detail: 'activables un par un' },
  { value: '8',  label: 'critères',   detail: 'de scoring pondérés' },
  { value: '20', label: 'CV',         detail: 'classés en une passe' },
  { value: '3',  label: 'connecteurs', detail: 'CRM natifs' },
];

const WORKFLOW_STEPS = [
  {
    step: '01',
    Icon: UploadCloud,
    title: 'Vous déposez',
    description:
      'Un CV, une pile de CV, une fiche de poste, ou juste vos notes de brief. PDF, Word ou PowerPoint — glisser-déposer, rien à ressaisir.',
  },
  {
    step: '02',
    Icon: BrainCircuit,
    title: "L'IA structure",
    description:
      "Extraction, lecture sémantique et mise en forme automatiques. Le traitement continue en arrière-plan : vous pouvez fermer l'onglet.",
  },
  {
    step: '03',
    Icon: FileOutput,
    title: 'Vous récupérez',
    description:
      'Un livrable prêt à envoyer — dossier PowerPoint à votre charte, fiche Word, rapport de matching — et la fiche poussée dans votre CRM.',
  },
];

const MODULES = [
  {
    key: 'generator',
    Icon: FileBadge,
    eyebrow: 'Générateur',
    title: 'Un CV brut devient un dossier de compétences à votre charte',
    description:
      "Anonymisation, structuration et mise en page automatiques. Vous relisez et ajustez le résultat directement dans le navigateur, puis vous exportez ou vous envoyez par mail.",
    points: [
      'Jusqu’à 18 missions sélectionnées par pertinence',
      'Trois templates, vos couleurs, votre logo',
      'Export PowerPoint éditable · Français et anglais',
    ],
    featured: true,
  },
  {
    key: 'matching',
    Icon: Scale,
    eyebrow: 'Matching CV / Offre',
    title: 'Savoir avant de positionner si le profil tient la route',
    description:
      "Un score sur 100 réparti en 8 critères pondérés, un verdict argumenté, les forces et les points de vigilance — plus l’argumentaire commercial et les questions d’entretien qui vont avec.",
    points: [
      'Classement de 20 CV face à une même fiche de poste',
      'Matrice de risques : flight risk, montée en charge',
      'Export PDF, envoi par mail, pitch WhatsApp',
    ],
    featured: true,
  },
  {
    key: 'fiche_poste',
    Icon: NotebookPen,
    eyebrow: 'Fiche de poste',
    title: 'Vos notes de brief deviennent une fiche exploitable',
    description:
      "Trois lignes griffonnées après un appel client, ou un document existant à remettre au propre : vous récupérez une fiche standardisée, exportable en Word.",
    points: [],
  },
  {
    key: 'audit_cv',
    Icon: ScanSearch,
    eyebrow: 'Audit CV',
    title: 'Repérer les incohérences avant votre client',
    description:
      "Lecture sémantique complète du CV : compétences techniques et comportementales, trous de parcours, points à creuser en entretien.",
    points: [],
  },
  {
    key: 'job_search',
    Icon: Briefcase,
    eyebrow: 'Recherche d’offres',
    title: 'Les missions du marché, testables sur vos profils',
    description:
      "Un vivier d’offres consolidé et filtrable, vos favoris, vos offres privées — et le matching lançable directement depuis l’annonce.",
    points: [],
  },
];

const AUDIENCES = [
  {
    Icon: Building2,
    title: 'ESN & sociétés de conseil',
    description:
      "Industrialisez la production des dossiers de compétences et arrêtez de positionner à l’instinct.",
  },
  {
    Icon: UserCheck,
    title: 'Cabinets de recrutement',
    description:
      'Triez une pile de candidatures en une passe et arrivez en shortlist avec des arguments écrits.',
  },
  {
    Icon: Users,
    title: 'Équipes RH internes',
    description:
      'Rédigez vos fiches de poste et qualifiez vos candidatures sans y passer vos après-midi.',
  },
  {
    Icon: Rocket,
    title: 'Freelances & indépendants',
    description:
      'Un dossier professionnel prêt à envoyer, et les missions du marché confrontées à votre profil.',
  },
];

const INTEGRATIONS = [
  { code: 'BM', name: 'BoondManager', desc: 'Création et mise à jour de candidats' },
  { code: 'HS', name: 'HubSpot',      desc: 'Contacts et fichiers synchronisés' },
  { code: 'MH', name: 'MyHubConsulting', desc: 'Import via clé API' },
];

const SECURITY_ITEMS = [
  {
    Icon: BrainCircuit,
    title: 'Vos données n’entraînent aucun modèle',
    content:
      "Les CV, notes et fiches que vous déposez servent uniquement à produire le livrable demandé. Ils ne rejoignent jamais un jeu d’entraînement, public ou interne.",
  },
  {
    Icon: Server,
    title: 'Hébergement européen chez Microsoft Azure',
    content:
      "L’infrastructure est opérée sur Azure, avec chiffrement des documents en transit et pendant le traitement. Chaque module s’exécute dans un environnement isolé.",
  },
  {
    Icon: Lock,
    title: 'Accès cloisonnés, module par module',
    content:
      "Vous décidez qui accède à quoi : chaque utilisateur reçoit ses propres droits, et les offres confidentielles restent invisibles pour ceux qui n’y ont pas accès.",
  },
];

const FAQ_ITEMS = [
  {
    q: 'Mes CV servent-ils à entraîner votre IA ?',
    a: "Non. Les documents que vous déposez sont traités pour produire votre livrable, puis conservés uniquement le temps de vous les restituer. Ils n’alimentent aucun entraînement de modèle.",
  },
  {
    q: 'Faut-il prendre les cinq modules ?',
    a: "Non. Chaque module s’active indépendamment sur votre compte, et vous choisissez ensuite qui, dans votre équipe, y a accès. Beaucoup démarrent avec le Générateur seul, puis ajoutent le Matching.",
  },
  {
    q: 'Le dossier généré est-il modifiable ?',
    a: "Oui. Un éditeur intégré vous permet de corriger chaque bloc de texte directement dans le navigateur, avant de télécharger le PowerPoint ou de l’envoyer par mail. Vous restez responsable de la validation finale.",
  },
  {
    q: 'Comment ça s’intègre à mon CRM ?',
    a: "BoondManager, HubSpot et MyHubConsulting sont connectés nativement : vous collez vos clés d’API une fois dans les réglages, et les candidats traités remontent automatiquement.",
  },
  {
    q: 'Quels formats de fichiers sont acceptés ?',
    a: "PDF, Word (.docx) et PowerPoint (.pptx) pour les CV, PDF, Word et texte brut pour les fiches de poste. Les CV scannés passent par une reconnaissance de texte automatique.",
  },
  {
    q: 'Combien de temps prend une analyse ?',
    a: "Quelques dizaines de secondes à quelques minutes selon la longueur du document et la charge. Le traitement tourne en arrière-plan : vous pouvez fermer l’onglet et revenir plus tard, le résultat vous attend.",
  },
];

const CONTACT_INFO = [
  { Icon: Phone,  title: 'Téléphone', content: '+33 6 12 01 81 41', link: 'tel:+33612018141' },
  { Icon: MapPin, title: 'Adresse',   content: 'Paris, France',     link: null },
  { Icon: Clock,  title: 'Horaires',  content: '8h30 – 18h30',      link: null },
];

/* ═══════════════════════════════════════════════════════════
   MOCKUPS PRODUIT — 100 % CSS/SVG, aucune capture d'écran
   (la démo animée du hero vit dans components/home/HeroDemo)
   ═══════════════════════════════════════════════════════════ */

// Ordre de dépôt : les scores tombent dans cet ordre, la liste se reclasse
const BATCH_ROWS = [
  { name: 'Consultant_A.pdf', score: 64 },
  { name: 'Consultant_B.pdf', score: 91 },
  { name: 'Consultant_C.pdf', score: 38 },
  { name: 'Consultant_D.pdf', score: 78 },
  { name: 'Consultant_E.pdf', score: 52 },
];

// Une seule « scène » : attente, 5 résultats, puis pause avant de rejouer
const BATCH_DURATIONS = [[900, 750, 750, 750, 750, 5500]];

/** Aperçu « classement de CV » : les scores arrivent et la liste se reclasse en direct. */
const BatchPreview = () => {
  const rootRef = useRef(null);
  const { running, reduced } = useAnimationGate(rootRef);
  const { step, round } = useSceneLoop(BATCH_DURATIONS, running);

  const revealed = reduced ? BATCH_ROWS.length : Math.min(step, BATCH_ROWS.length);
  const ranks = rankRows(BATCH_ROWS, revealed);
  const best = BATCH_ROWS.slice(0, revealed).reduce((m, r) => Math.max(m, r.score), 0);

  return (
    <div
      ref={rootRef}
      className={`${styles.screenshot} ${running ? '' : styles.paused}`}
      aria-hidden="true"
    >
      <div className={styles.screenChrome}>
        <span className={styles.mockDot} />
        <span className={styles.mockDot} />
        <span className={styles.mockDot} />
        <span className={styles.screenTabs}>
          <span className={styles.screenTabActive}>Classement de CV</span>
          <span className={styles.screenTab}>Historique</span>
        </span>
      </div>

      <div className={styles.screenBody}>
        <div className={styles.screenHead}>
          <div>
            <p className={styles.screenTitle}>Classement des candidats</p>
            <p className={styles.screenSub}>5 CV comparés à « Data Engineer — Nantes »</p>
          </div>
          <span className={`${styles.screenChip} ${revealed ? '' : styles.screenChipPending}`}>
            <Trophy size={12} strokeWidth={2} />
            {revealed ? `Meilleur : ${best} %` : 'Analyse en cours…'}
          </span>
        </div>

        {/* La clé remonte la liste à chaque tour pour rejouer l'arrivée des CV */}
        <div key={round} className={styles.screenRows} style={{ '--rows': BATCH_ROWS.length }}>
          {BATCH_ROWS.map((row, i) => {
            const scored = i < revealed;
            const rank = ranks[i];
            const top = scored && rank === 0;
            const tone = scoreTone(row.score);
            return (
              <div
                key={row.name}
                className={[
                  styles.screenRow,
                  scored ? styles.screenRowScored : '',
                  top ? styles.screenRowTop : '',
                ].join(' ')}
                style={{ '--rank': rank, '--i': i }}
              >
                <span className={styles.screenRank}>
                  {top ? <Trophy size={13} strokeWidth={2} /> : scored ? `#${rank + 1}` : '–'}
                </span>
                <span className={styles.screenName}>{row.name}</span>
                <span className={styles.screenTrack}>
                  <span
                    className={`${styles.screenFill} ${styles[`tone_${tone}`]}`}
                    style={{ '--s': row.score / 100 }}
                  />
                </span>
                {scored ? (
                  <span className={`${styles.screenScore} ${styles[`toneText_${tone}`]}`}>
                    {row.score} %
                  </span>
                ) : (
                  <span className={styles.screenPending}>Analyse…</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   COMPOSANT PRINCIPAL
   ═══════════════════════════════════════════════════════════ */

const HomePage = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled]             = useState(false);
  const [visibleSections, setVisibleSections]   = useState(() => new Set());
  const [openFaq, setOpenFaq]                   = useState(0);

  const sectionRefs        = useRef([]);
  const mobileMenuRef      = useRef(null);
  const mobileMenuButtonRef = useRef(null);
  const heroRef            = useRef(null);
  const howRef             = useRef(null);

  // Animations en boucle figées quand leur section n'est pas à l'écran
  const { running: heroRunning } = useAnimationGate(heroRef);
  const { running: howRunning }  = useAnimationGate(howRef);

  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  // ── Ombre de la navbar au scroll ────────────────────────────
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ── Révélation des sections (IntersectionObserver) ──────────
  useEffect(() => {
    // Filet de sécurité : navigateur sans IntersectionObserver → tout est visible
    if (typeof IntersectionObserver === 'undefined') {
      setVisibleSections(new Set(sectionRefs.current.map((el) => el.id)));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );

    sectionRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // ── Menu mobile : clic extérieur, Échap, scroll bloqué ──────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        mobileMenuButtonRef.current &&
        !mobileMenuButtonRef.current.contains(event.target)
      ) {
        closeMobileMenu();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && isMobileMenuOpen) closeMobileMenu();
    };

    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen, closeMobileMenu]);

  const addToRefs = (el) => {
    if (el && !sectionRefs.current.includes(el)) sectionRefs.current.push(el);
  };

  const isVisible = (id) => visibleSections.has(id);
  const year = new Date().getFullYear();

  return (
    <div className={styles.home}>

      {/* ══════════════ NAVBAR ══════════════ */}
      <header className={`${styles.navbar} ${isScrolled ? styles.navbarScrolled : ''}`}>
        <div className={styles.navbarContainer}>
          <a href="#top" className={styles.navbarBrand} aria-label="CVtoSkill — accueil">
            <img src={logo} alt="" className={styles.navbarLogo} />
            <span className={styles.navbarBrandName}>CVtoSkill</span>
          </a>

          <nav className={styles.navbarNav} aria-label="Navigation principale">
            {NAV_LINKS.map(({ href, label }) => (
              <a key={href} href={href} className={styles.navLink}>
                {label}
              </a>
            ))}
          </nav>

          <div className={styles.navbarActions}>
            <Link to="/login" className={styles.navGhostBtn}>
              Se connecter
            </Link>
            <Link to="/demo" className={styles.navPrimaryBtn}>
              <span>Demander une démo</span>
              <ArrowRight size={15} strokeWidth={2.25} />
            </Link>

            <button
              ref={mobileMenuButtonRef}
              className={`${styles.mobileMenuButton} ${isMobileMenuOpen ? styles.mobileMenuOpen : ''}`}
              onClick={() => setIsMobileMenuOpen((p) => !p)}
              aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={isMobileMenuOpen}
              type="button"
            >
              {isMobileMenuOpen
                ? <X size={20} strokeWidth={2.25} />
                : <Menu size={20} strokeWidth={2.25} />}
            </button>
          </div>
        </div>
      </header>

      {/* ══════════════ MENU MOBILE ══════════════ */}
      <div
        ref={mobileMenuRef}
        className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.mobileMenuActive : ''}`}
        aria-hidden={!isMobileMenuOpen}
      >
        <nav className={styles.mobileMenuContent}>
          {NAV_LINKS.map(({ href, label }) => (
            <a key={href} href={href} className={styles.mobileMenuLink} onClick={closeMobileMenu}>
              {label}
              <ArrowUpRight size={16} strokeWidth={2} />
            </a>
          ))}
          <a href="#contact" className={styles.mobileMenuLink} onClick={closeMobileMenu}>
            Contact
            <ArrowUpRight size={16} strokeWidth={2} />
          </a>

          <div className={styles.mobileMenuFooter}>
            <Link to="/login" className={styles.mobileGhostBtn} onClick={closeMobileMenu}>
              Se connecter
            </Link>
            <Link to="/demo" className={styles.mobilePrimaryBtn} onClick={closeMobileMenu}>
              Demander une démo
              <ArrowRight size={16} strokeWidth={2.25} />
            </Link>
          </div>
        </nav>
      </div>

      {/* ══════════════ HERO ══════════════ */}
      <section
        ref={heroRef}
        className={`${styles.hero} ${heroRunning ? '' : styles.paused}`}
        id="top"
      >
        <div className={styles.heroBackground} aria-hidden="true">
          <div className={styles.heroOrb} style={{ '--delay': '0s' }} />
          <div className={styles.heroOrb} style={{ '--delay': '2.5s' }} />
          <div className={styles.heroOrb} style={{ '--delay': '5s' }} />
          <div className={styles.heroGrid} />
        </div>

        <div className={`${styles.container} ${styles.heroContent}`}>
          <div className={styles.heroText}>
            <span className={styles.badge}>
              <span className={styles.pulse} />
              5 modules RH · une seule plateforme
            </span>

            <h1 className={styles.heroTitle}>
              Le travail de mise en forme,
              <span className={styles.gradientText}> l’IA s’en charge.</span>
            </h1>

            <p className={styles.heroSubtitle}>
              Dossiers de compétences, fiches de poste, audits de CV, matching et
              recherche d’offres. Cinq outils qui se parlent, pour que votre équipe
              passe son temps sur les candidats plutôt que sur les fichiers.
            </p>

            <div className={styles.heroButtons}>
              <Link to="/demo" className={styles.ctaButton}>
                Demander une démo
                <ArrowRight size={17} strokeWidth={2.25} />
              </Link>
              <a href="#how" className={styles.secondaryButton}>
                Voir comment ça marche
              </a>
            </div>

            <ul className={styles.heroTrust}>
              <li><CheckCircle2 size={14} strokeWidth={2.5} /> Gratuit et sans engagement</li>
              <li><CheckCircle2 size={14} strokeWidth={2.5} /> Réponse sous 24 h</li>
              <li><CheckCircle2 size={14} strokeWidth={2.5} /> Hébergement Azure</li>
            </ul>
          </div>

          <div className={styles.heroVisual}>
            <HeroDemo />
            <div className={styles.floatingCard}>
              <CheckCircle2 size={16} strokeWidth={2.25} className={styles.floatingCardIcon} />
              <span>Dossier généré · 2 min</span>
            </div>
          </div>
        </div>

        {/* Bandeau de preuves */}
        <div className={styles.heroStats}>
          <div className={styles.container}>
            <div className={styles.statsGrid}>
              {PROOF_STATS.map(({ value, label, detail }) => (
                <div key={label} className={styles.statItem}>
                  <div className={styles.statNumber}>{value}</div>
                  <div className={styles.statLabel}>{label}</div>
                  <div className={styles.statDetail}>{detail}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ VIDÉO DE PRÉSENTATION ══════════════ */}
      <section className={styles.video} id="video" ref={addToRefs}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionBadge}>En vidéo</span>
            <h2 className={styles.sectionTitle}>Le tour de la plateforme en 2 minutes</h2>
            <p className={styles.sectionSubtitle}>
              Du CV déposé au dossier PowerPoint, de l’audit au matching : chaque
              module en action, tel que vous le retrouverez dans l’application.
            </p>
          </div>

          <div className={`${styles.videoVisual} ${isVisible('video') ? styles.isVisible : ''}`}>
            <ProductVideo />
          </div>
        </div>
      </section>

      {/* ══════════════ COMMENT ÇA MARCHE ══════════════ */}
      <section
        className={styles.how}
        id="how"
        ref={(el) => { howRef.current = el; addToRefs(el); }}
      >
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionBadge}>Le principe</span>
            <h2 className={styles.sectionTitle}>Trois gestes, un livrable</h2>
            <p className={styles.sectionSubtitle}>
              Aucune installation, aucun paramétrage préalable. Vous ouvrez la
              plateforme, vous déposez un fichier, vous récupérez un document
              présentable.
            </p>
          </div>

          <div
            className={[
              styles.workflow,
              isVisible('how') ? styles.isVisible : '',
              howRunning ? '' : styles.paused,
            ].join(' ')}
          >
            {WORKFLOW_STEPS.map(({ step, Icon, title, description }, index) => (
              <React.Fragment key={step}>
                <div className={styles.stationCard} style={{ '--i': index }}>
                  <span className={styles.stationNumber}>{step}</span>
                  <div className={styles.stationIcon}>
                    <Icon size={22} strokeWidth={1.75} />
                  </div>
                  <h3 className={styles.stationTitle}>{title}</h3>
                  <p className={styles.stationDescription}>{description}</p>
                </div>
                {index < WORKFLOW_STEPS.length - 1 && (
                  <div className={styles.stationConnector} style={{ '--i': index }} aria-hidden="true">
                    <span className={styles.connectorLine} />
                    <span className={styles.connectorDot} />
                    <ArrowRight size={16} strokeWidth={2} className={styles.connectorArrow} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ MODULES ══════════════ */}
      <section className={styles.modules} id="modules" ref={addToRefs}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionBadge}>Les modules</span>
            <h2 className={styles.sectionTitle}>Cinq outils, activables un par un</h2>
            <p className={styles.sectionSubtitle}>
              Chacun règle un problème précis. Ensemble, ils couvrent la chaîne
              complète : du CV reçu jusqu’au candidat positionné.
            </p>
          </div>

          <div className={`${styles.moduleGrid} ${isVisible('modules') ? styles.isVisible : ''}`}>
            {MODULES.map(({ key, Icon, eyebrow, title, description, points, featured }, index) => (
              <article
                key={key}
                className={`${styles.moduleCard} ${featured ? styles.moduleCardFeatured : ''}`}
                style={{ '--i': index }}
              >
                <div className={styles.moduleTop}>
                  <span className={styles.moduleIcon}>
                    <Icon size={20} strokeWidth={1.75} />
                  </span>
                  <span className={styles.moduleEyebrow}>{eyebrow}</span>
                </div>

                <h3 className={styles.moduleTitle}>{title}</h3>
                <p className={styles.moduleDescription}>{description}</p>

                {points.length > 0 && (
                  <ul className={styles.modulePoints}>
                    {points.map((point) => (
                      <li key={point}>
                        <CheckCircle2 size={14} strokeWidth={2.25} />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ APERÇU PRODUIT — classement live ══════════════ */}
      <section className={styles.preview} id="preview" ref={addToRefs}>
        <div className={styles.container}>
          <div className={styles.previewLayout}>
            <div className={styles.previewText}>
              <span className={styles.sectionBadge}>Dans l’interface</span>
              <h2 className={styles.sectionTitleLeft}>
                Une pile de CV, un classement
              </h2>
              <p className={styles.previewLead}>
                Déposez une fiche de poste et jusqu’à vingt CV. Chaque profil est
                analysé séparément, puis reclassé par score au fur et à mesure que
                les résultats tombent.
              </p>

              <ul className={styles.previewList}>
                <li>
                  <Sparkles size={15} strokeWidth={2} />
                  <span>
                    <strong>Le classement se met à jour en direct.</strong> Vous
                    voyez les premiers scores sans attendre la fin du lot.
                  </span>
                </li>
                <li>
                  <Zap size={15} strokeWidth={2} />
                  <span>
                    <strong>Chaque ligne s’ouvre sur l’analyse complète.</strong>{' '}
                    Détail par critère, écarts constatés, questions à poser.
                  </span>
                </li>
                <li>
                  <Clock size={15} strokeWidth={2} />
                  <span>
                    <strong>Le traitement survit à la fermeture de l’onglet.</strong>{' '}
                    Vous reprenez là où vous en étiez.
                  </span>
                </li>
              </ul>

              <Link to="/demo" className={styles.inlineCta}>
                Voir sur vos propres documents
                <ArrowRight size={16} strokeWidth={2.25} />
              </Link>
            </div>

            <div className={`${styles.previewVisual} ${isVisible('preview') ? styles.isVisible : ''}`}>
              <BatchPreview />
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ POUR QUI ══════════════ */}
      <section className={styles.audience} id="audience" ref={addToRefs}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionBadge}>Pour qui</span>
            <h2 className={styles.sectionTitle}>Conçu pour ceux qui lisent des CV toute la journée</h2>
            <p className={styles.sectionSubtitle}>
              Le point commun : trop de documents à traiter, trop peu de temps
              pour les mettre en forme.
            </p>
          </div>

          <div className={styles.audienceGrid}>
            {AUDIENCES.map(({ Icon, title, description }) => (
              <div key={title} className={styles.audienceCard}>
                <span className={styles.audienceIcon}>
                  <Icon size={19} strokeWidth={1.75} />
                </span>
                <h3 className={styles.audienceTitle}>{title}</h3>
                <p className={styles.audienceDescription}>{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ INTÉGRATIONS ══════════════ */}
      <section className={styles.integrations} ref={addToRefs} id="integrations">
        <div className={styles.container}>
          <div className={styles.integrationsCard}>
            <div className={styles.integrationsText}>
              <span className={styles.integrationsBadge}>
                <Plug size={13} strokeWidth={2.25} />
                Connecteurs
              </span>
              <h2 className={styles.integrationsTitle}>
                Le candidat traité arrive directement dans votre CRM
              </h2>
              <p className={styles.integrationsDesc}>
                Vous collez vos clés d’API une fois dans les réglages. Ensuite,
                chaque profil passé par la plateforme y est créé ou mis à jour
                automatiquement — plus de double saisie.
              </p>
            </div>

            <div className={styles.integrationsList}>
              {INTEGRATIONS.map(({ code, name, desc }) => (
                <div key={code} className={styles.integrationRow}>
                  <span className={styles.integrationAvatar}>{code}</span>
                  <div className={styles.integrationInfo}>
                    <strong>{name}</strong>
                    <small>{desc}</small>
                  </div>
                  <CheckCircle2 size={16} strokeWidth={2} className={styles.integrationCheck} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ SÉCURITÉ ══════════════ */}
      <section className={styles.security} id="security" ref={addToRefs}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={`${styles.sectionBadge} ${styles.sectionBadgeDark}`}>Sécurité</span>
            <h2 className={`${styles.sectionTitle} ${styles.sectionTitleLight}`}>
              Ce que deviennent vos documents
            </h2>
            <p className={`${styles.sectionSubtitle} ${styles.sectionSubtitleLight}`}>
              Un CV est une donnée personnelle et un dossier client est un actif
              stratégique. Les deux méritent d’être traités comme tels.
            </p>
          </div>

          <div className={styles.securityGrid}>
            {SECURITY_ITEMS.map(({ Icon, title, content }) => (
              <div key={title} className={styles.securityCard}>
                <div className={styles.securityIcon}>
                  <Icon size={22} strokeWidth={1.75} />
                </div>
                <h3 className={styles.securityTitle}>{title}</h3>
                <p className={styles.securityText}>{content}</p>
              </div>
            ))}
          </div>

          <p className={styles.securityFootnote}>
            <ShieldCheck size={15} strokeWidth={2} />
            Le détail du traitement figure dans nos{' '}
            <Link to="/mentions-legales">mentions légales</Link> et nos{' '}
            <Link to="/cgu">conditions d’utilisation</Link>.
          </p>
        </div>
      </section>

      {/* ══════════════ FAQ ══════════════ */}
      <section className={styles.faq} id="faq" ref={addToRefs}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionBadge}>Questions fréquentes</span>
            <h2 className={styles.sectionTitle}>Ce qu’on nous demande le plus souvent</h2>
          </div>

          <div className={styles.faqList}>
            {FAQ_ITEMS.map(({ q, a }, index) => {
              const isOpen = openFaq === index;
              return (
                <div key={q} className={`${styles.faqItem} ${isOpen ? styles.faqItemOpen : ''}`}>
                  <button
                    type="button"
                    className={styles.faqQuestion}
                    onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${index}`}
                  >
                    <span>{q}</span>
                    <span className={styles.faqToggle} aria-hidden="true">
                      {isOpen
                        ? <Minus size={15} strokeWidth={2.5} />
                        : <Plus  size={15} strokeWidth={2.5} />}
                    </span>
                  </button>
                  <div
                    id={`faq-answer-${index}`}
                    className={styles.faqAnswerWrap}
                    role="region"
                  >
                    <p className={styles.faqAnswer}>{a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════ CTA FINAL ══════════════ */}
      <section className={styles.cta}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>
              Vingt minutes suffisent pour voir si ça vous fait gagner du temps
            </h2>
            <p className={styles.ctaSubtitle}>
              On prend vos propres documents, on les passe dans la plateforme, et
              vous jugez sur le résultat. Sans engagement.
            </p>
            <div className={styles.ctaActions}>
              <Link to="/demo" className={styles.ctaButton}>
                Demander une démo
                <ArrowRight size={17} strokeWidth={2.25} />
              </Link>
              <Link to="/login" className={styles.secondaryButton}>
                J’ai déjà un compte
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ CONTACT ══════════════ */}
      <section className={styles.contact} id="contact" ref={addToRefs}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionBadge}>Contact</span>
            <h2 className={styles.sectionTitle}>Parlons de votre organisation</h2>
            <p className={styles.sectionSubtitle}>
              Une question sur un module, un besoin d’intégration particulier ?
              Écrivez-nous ou appelez-nous directement.
            </p>
          </div>

          <div className={styles.contactGrid}>
            {CONTACT_INFO.map(({ Icon, title, content, link }) => (
              <div key={title} className={styles.contactCard}>
                <div className={styles.contactIcon}>
                  <Icon size={20} strokeWidth={1.75} />
                </div>
                <h3 className={styles.contactTitle}>{title}</h3>
                {link ? (
                  <a href={link} className={styles.contactLink}>{content}</a>
                ) : (
                  <p className={styles.contactText}>{content}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ FOOTER ══════════════ */}
      <footer className={styles.footer}>
        <div className={styles.footerTopBar} aria-hidden="true" />
        <div className={styles.container}>
          <div className={styles.footerContent}>
            <div className={styles.footerBrand}>
              <div className={styles.footerBrandLogo}>
                <img src={logo} alt="" className={styles.footerLogo} />
                <span className={styles.footerBrandName}>CVtoSkill</span>
              </div>
              <p className={styles.footerDescription}>
                Cinq modules IA pour produire, qualifier et positionner —
                sans quitter une seule interface.
              </p>
              <span className={styles.footerBadge}>
                <Zap size={12} strokeWidth={2.5} />
                Conçu et opéré en France
              </span>
            </div>

            <div className={styles.footerLinks}>
              <div className={styles.footerColumn}>
                <h4>Produit</h4>
                <a href="#modules">Les 5 modules</a>
                <a href="#how">Comment ça marche</a>
                <a href="#integrations">Connecteurs CRM</a>
                <a href="#faq">Questions fréquentes</a>
              </div>

              <div className={styles.footerColumn}>
                <h4>Entreprise</h4>
                <a href="#audience">Pour qui</a>
                <a href="#security">Sécurité</a>
                <a href="#contact">Contact</a>
                <Link to="/demo">Demander une démo</Link>
              </div>

              <div className={styles.footerColumn}>
                <h4>Légal</h4>
                <Link to="/mentions-legales">Mentions légales</Link>
                <Link to="/cgu">Conditions d’utilisation</Link>
                <Link to="/cgu#donnees">Politique de confidentialité</Link>
                <Link to="/login">Connexion</Link>
              </div>
            </div>
          </div>

          <div className={styles.footerBottom}>
            <p className={styles.copyright}>
              © {year} CVtoSkill — Tous droits réservés
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;